import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useSession } from "@/hooks/useSession";
import { Button } from "@/components/ui/button";
import { ChatThread } from "./ChatThread";
import { ChatScrollButton } from "./ChatScrollButton";
import { MessageInput } from "./MessageInput";
import { ThinkingIndicator } from "./ThinkingIndicator";
import { useMessages } from "@/hooks/useMessages";
import { useMessageRealtime } from "@/hooks/useMessageRealtime";
import { useScrollManagement } from "@/hooks/useScrollManagement";
import { useMessageEditor } from "@/hooks/useMessageEditor";
import { useAIChat } from "@/hooks/useAIChat";

export const ChatContainer = () => {
  const { user } = useSession();
  const userId = user?.id || null;
  const [summaryText, setSummaryText] = useState("");

  const {
    isUserAtBottom,
    hasNewMessages,
    setHasNewMessages,
    messagesEndRef,
    messagesContainerRef,
    checkIfAtBottom,
    scrollToBottom,
  } = useScrollManagement();

  const {
    messages,
    setMessages,
    isLoadingMore,
    hasMoreMessages,
    loadMessages,
    loadMoreMessages,
  } = useMessages(userId);

  const {
    editingMessageIds,
    setEditingMessageIds,
    workInProgressIds,
    setWorkInProgressIds,
    handleEditMessage,
    handleCancelEdit,
    handleSaveEdit,
  } = useMessageEditor(messages);

  const {
    isLoading,
    showThinking,
    sendMessageAndGetAIResponse,
  } = useAIChat({
    userId,
    messages,
    checkIfAtBottom,
    setHasNewMessages,
    setEditingMessageIds,
    setWorkInProgressIds,
  });

  useMessageRealtime({
    userId,
    setMessages,
    checkIfAtBottom,
    scrollToBottom,
    setHasNewMessages,
    messagesContainerRef,
  });

  useEffect(() => {
    if (userId) {
      console.log('[INIT] Loading messages for userId:', userId);
      loadMessages();
      setTimeout(() => {
        console.log('[INIT] Initial scroll to bottom');
        scrollToBottom();
      }, 100);
    }
  }, [userId, loadMessages, scrollToBottom]);

  useEffect(() => {
    if (showThinking && isUserAtBottom) {
      console.log('[THINKING] Indicator appeared, scrolling to bottom');
      setTimeout(() => scrollToBottom(), 100);
    }
  }, [showThinking, isUserAtBottom, scrollToBottom]);

  const handleSendMessage = async (content: string) => {
    await sendMessageAndGetAIResponse(content);
  };

  const handleSendSummary = async (messageId: string, summary: string) => {
    await sendMessageAndGetAIResponse(summary, { originalMessageId: messageId });
  };

  const handleSummarize = (summary: string) => {
    setSummaryText(summary);
    toast.success("Summary added to message input");
  };

  return (
    <div className="flex flex-col h-screen bg-background" role="main" aria-label="Chat application">
      <div ref={messagesContainerRef} className="flex-1 overflow-auto" role="log" aria-label="Message history" aria-live="polite">
        <div />
        {hasMoreMessages && (
          <div className="p-4 flex justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={loadMoreMessages}
              disabled={isLoadingMore}
              aria-label="Load older messages"
            >
              {isLoadingMore ? "Loading..." : "Load Older Messages"}
            </Button>
          </div>
        )}
        <ChatThread
          messages={messages}
          editingMessageIds={editingMessageIds}
          onEditMessage={handleEditMessage}
          onCancelEdit={handleCancelEdit}
          onSaveEdit={handleSaveEdit}
          onSummarize={handleSummarize}
          onSend={handleSendSummary}
        />
        {showThinking && (
          <div className="p-4">
            <ThinkingIndicator />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {hasNewMessages && !isUserAtBottom && (
        <ChatScrollButton onClick={scrollToBottom} />
      )}
      
      <div className="border-t bg-background" role="region" aria-label="Message input">
        <MessageInput onSend={handleSendMessage} isLoading={isLoading} summaryText={summaryText} />
      </div>
    </div>
  );
};
